/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    IRequestContext
} from '@msdyn365-commerce/core';
import {
    buildCacheKeyWithUrlTokens,
    getFullProductList,
    GetFullProductListInput,
    QuickOrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';

/**
 * Input class for getActiveOrderTemplateLines data action.
 */
export class ActiveProductListInput implements IActionInput {
    public requestContext: IRequestContext;

    public productListId: string | undefined;

    public constructor(requestContext: IRequestContext, productListId?: string) {
        this.requestContext = requestContext;
        this.productListId = productListId;
    }

    /**
     * GetCacheObjectType.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => buildCacheKeyWithUrlTokens('ActiveProductList', this.requestContext);

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheObjectType = (): string => 'ActiveProductList';

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    public dataCacheType = (): CacheType => 'instance';

    /**
     * Set Cache output.
     * @returns - Flag for cache output.
     */
    public shouldCacheOutput = (): boolean => false;
}

/**
 * Calls the Retail API and returns a ProductList object for user.
 * @param input - ProductList Input.
 * @param context - ActionContext.
 * @returns - OrderTemplate.
 */
export async function getActiveProductList(input: ActiveProductListInput, context: IActionContext): Promise<QuickOrderTemplate> {
    const fullProductListInput = new GetFullProductListInput(input.productListId);
    const productList = await getFullProductList(fullProductListInput, context);
    return productList;
}

/**
 * Calls the Retail API and returns a ProductList object for user.
 * @param input - ProductList Input.
 * @param context - ActionContext.
 * @returns - ProductList.
 */
export const getActiveProductListDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/quick-order-grid/get-product-list',
    action: getActiveProductList as IAction<QuickOrderTemplate>,

    /**
     * Method to call for input.
     * @param context - ActionContext.
     * @returns - Input for action..
     */
    input: (context: ICreateActionContext) => {
        return new ActiveProductListInput(context.requestContext);
    }
});

export default getActiveProductListDataAction;
